import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchQuotes = createAsyncThunk(
  'quotes/fetchQuotes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.getQuotes(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createQuote = createAsyncThunk(
  'quotes/createQuote',
  async (quoteData, { rejectWithValue }) => {
    try {
      const response = await api.createQuote(quoteData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateQuote = createAsyncThunk(
  'quotes/updateQuote',
  async ({ quoteId, quoteData }, { rejectWithValue }) => {
    try {
      const response = await api.updateQuote(quoteId, quoteData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const acceptQuote = createAsyncThunk(
  'quotes/acceptQuote',
  async (quoteId, { rejectWithValue }) => {
    try {
      const response = await api.acceptQuote(quoteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const rejectQuote = createAsyncThunk(
  'quotes/rejectQuote',
  async (quoteId, { rejectWithValue }) => {
    try {
      const response = await api.rejectQuote(quoteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  quotes: [],
  currentQuote: null,
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {
    status: null,
    vendor_id: null,
    customer_id: null,
  },
};

const quoteSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentQuote: (state, action) => {
      state.currentQuote = action.payload;
    },
    clearCurrentQuote: (state) => {
      state.currentQuote = null;
    },
    updateQuoteStatus: (state, action) => {
      const { quoteId, status, respondedAt } = action.payload;
      const quote = state.quotes.find(q => q.id === quoteId);
      if (quote) {
        quote.status = status;
        quote.responded_at = respondedAt;
      }
      if (state.currentQuote && state.currentQuote.id === quoteId) {
        state.currentQuote.status = status;
        state.currentQuote.responded_at = respondedAt;
      }
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        vendor_id: null,
        customer_id: null,
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Quotes
      .addCase(fetchQuotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.data) {
          state.quotes = data.data;
          state.pagination = {
            current: data.current_page || 1,
            pageSize: data.per_page || 10,
            total: data.total || 0,
          };
        } else {
          state.quotes = Array.isArray(data) ? data : [];
        }
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Quote
      .addCase(createQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes.unshift(action.payload);
        state.currentQuote = action.payload;
      })
      .addCase(createQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Quote
      .addCase(updateQuote.fulfilled, (state, action) => {
        const updatedQuote = action.payload;
        const index = state.quotes.findIndex(q => q.id === updatedQuote.id);
        if (index !== -1) {
          state.quotes[index] = updatedQuote;
        }
        if (state.currentQuote && state.currentQuote.id === updatedQuote.id) {
          state.currentQuote = updatedQuote;
        }
      })
      // Accept Quote
      .addCase(acceptQuote.fulfilled, (state, action) => {
        const acceptedQuote = action.payload;
        const index = state.quotes.findIndex(q => q.id === acceptedQuote.id);
        if (index !== -1) {
          state.quotes[index] = acceptedQuote;
        }
        if (state.currentQuote && state.currentQuote.id === acceptedQuote.id) {
          state.currentQuote = acceptedQuote;
        }
      })
      // Reject Quote
      .addCase(rejectQuote.fulfilled, (state, action) => {
        const rejectedQuote = action.payload;
        const index = state.quotes.findIndex(q => q.id === rejectedQuote.id);
        if (index !== -1) {
          state.quotes[index] = rejectedQuote;
        }
        if (state.currentQuote && state.currentQuote.id === rejectedQuote.id) {
          state.currentQuote = rejectedQuote;
        }
      });
  },
});

export const {
  clearError,
  setCurrentQuote,
  clearCurrentQuote,
  updateQuoteStatus,
  setFilters,
  clearFilters,
  setPagination,
} = quoteSlice.actions;

export default quoteSlice.reducer;
